import * as Sentry from '@sentry/gatsby';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { KeyboardEvent, memo, useEffect, useMemo, useRef, useState } from 'react';
import { object, string } from 'yup';

import Button from '~components/Button/Button';
import SpeakWithAgent from '~components/HeroPaidSearch/child-blocks/SpeakWithAgent';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { getLocationByZip } from '~components/ZipCodeForm/util';
import MarketoForm from '~globals/Marketo/marketoForm';
import { EMAIL_REGEXP, PHONE_REGEXP } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';
import errorIcon from '~images/common/error-icon.svg';
import { updateLead } from '~redux/Actions/GlobalActions';
import useAllianceId from '~hooks/useAllianceId';

import Spinner from '../../Spinner/Spinner';

import { createLead } from './query';

type Props = {
    uid: string;
    mktoFormId?: number;
    isMobileSideBySide?: boolean;
    isStacked?: boolean;
    classes?: string;
    buttonText?: string;
    waTracking?: string;
    errorTextColor?: string;
    leadInfo?: { advertiseSource: string; leadStatus?: string };
    leadTrackingAction?: { page: { action: string } };
    qaButton?: string;
    inputClasses?: string;
    buttonClasses?: string;
    beforeSubmit?: () => void;
    onSuccess?: () => void;
    onError?: () => void;
    ariaLabel?: string;
    buttonStyletype?: 'primary' | 'secondary' | 'reskin' | 'orange' | 'link' | 'rebrandAction';
    disabled?: boolean;
    labelTextColor?: string;
    formMarginTop?: string;
    isLabelHidden?: boolean;
    leadtype?: string;
    fixedallianceid?: string;
    ctacopy?: string;
};

type SelectOptions = {
    label: string;
    value: string;
    selected?: boolean;
};

type InputProps = {
    id: string;
    name: string;
    label: string;
    title?: string;
    placeholder?: string;
    disabled: boolean;
    required: boolean;
    minChar?: number;
    maxChar?: number;
    autoComplete?: string;
    type: string;
    qaInput: string;
    ariaLabel?: string;
    options?: SelectOptions[];
    onKeyUp?: (evt: KeyboardEvent<HTMLInputElement>) => void;
    invalidMsg?: string;
    fullWidth?: boolean;
    quarterWidth?: boolean;
};

interface FormValues {
    fname: string;
    lname: string;
    phone: string;
    email: string;
    company: string;
    zip: string;
    employees: string;
    [key: string]: string;
}

interface apiError {
    type?: string;
    message?: string;
    errorType?: string;
}

export const EmployerLeadFormSchema = object({
    fname: string()
        .matches(/^[a-zA-Z\s-']*$/g, 'Invalid name')
        .min(2, 'Too Short!')
        .max(50, 'Too Long!')
        .required('Required'),
    lname: string()
        .matches(/^[a-zA-Z\s-']*$/g, 'Invalid name')
        .min(2, 'Too Short!')
        .max(50, 'Too Long!')
        .required('Required'),
    email: string().matches(EMAIL_REGEXP, 'Invalid email').required('Required'),
    phone: string()
        .matches(PHONE_REGEXP, 'Invalid phone')
        .min(10, 'Too Short!')
        .max(10)
        .required('Required'),
    company: string().min(2, 'Too Short!').max(80, 'Too Long!'),
    zip: string()
        .min(5, 'Too Short!')
        .max(5, 'Too Long!')
        .required('Required')
        .test('is-valid-zip', 'Invalid zip', async (value) => {
            if (!value) {
                return false;
            }
            const response = await getLocationByZip(value);
            return !response?.data?.areaByZipCode?.hasError;
        }),
    employees: string().required('Required'),
});

const EmployerLeadForm = ({
    ariaLabel,
    beforeSubmit = () => undefined,
    buttonClasses = '',
    buttonStyletype,
    buttonText = 'Get a quote',
    classes = '',
    disabled = false,
    formMarginTop,
    labelTextColor,
    errorTextColor = 'text-red',
    inputClasses = '',
    isLabelHidden,
    isMobileSideBySide = false,
    isStacked = false,
    mktoFormId = 1004,
    onError = () => undefined,
    onSuccess = () => undefined,
    qaButton = 'get-a-quote',
    uid,
    waTracking = '',
    leadtype = '',
    fixedallianceid = '',
    ctacopy = '',
}: Props) => {
    // const initialEmployees = classes === 'small-group' ? '2' : '50';
    const initialEmployees = '2';
    // employer-and-individual
    // mktoFormId = process.env.GATSBY_SUBDOMAIN ? 1004 : mktoFormId;
    const EMPLOYEE_OPTIONS: { [key: string]: string } = {
        '2': '2-49',
        '50': '50+',
    };
    const INITIAL_VALUES = useRef<FormValues>({
        fname: '',
        lname: '',
        phone: '',
        email: '',
        company: '',
        zip: useAppSelector((state) => state.global.geoLocation?.zip),
        employees: initialEmployees,
    });
    const dispatch = useAppDispatch();
    const [id] = useState(() => uid || `email-${Math.random().toString(36).substring(2, 9)}`);
    const { location } = usePageContext();

    const [isLoading, setIsLoading] = useState(false);
    const [version, setVersion] = useState('a');
    const [wasSubmitted, setWasSubmitted] = useState(false);
    const [hasLoaded, sethasLoaded] = useState(false);

    const mktoForm = new MarketoForm(mktoFormId);
    const allid = useAppSelector((state) => state.global.allianceId);
    const selectedAllid = useAllianceId(false);
    const { isMobile } = useWindowSize();
    const isSmallGroup = location.pathname.includes('enroll/small-group');
    const findTestVersion = (attempts = 0) => {
        setTimeout(() => {
            if (attempts > 20) {
                return;
            }
            if (typeof window !== 'undefined' && window.ehimTest) {
                setVersion(window.ehimTest);
            } else {
                findTestVersion(attempts + 1);
            }
        }, 200);
    };

    const trackingComponentName = 'Employer Lead Form';
    if (!waTracking) waTracking = trackingComponentName + ': Get a Quote';

    useEffect(() => {
        findTestVersion();
    }, []);

    useEffect(() => {
        setTimeout(() => {
            mktoForm.init();
            sethasLoaded(true);
        }, 1000);
    }, []);

    const handleSubmit = async (values: FormValues): Promise<void> => {
        beforeSubmit();

        if (isLoading) return;

        setIsLoading(true);

        try {
            mktoForm.mktoFormSubmit(values.email, allid);
        } catch (err) {
            let sentryError;
            if (typeof err === 'string') {
                sentryError = new Error(err);
            } else if (err instanceof Error) {
                sentryError = new Error(err.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'EmployerLeadForm',
                        event: 'mktoFormSubmit',
                    },
                });
            }
        }

        const leadRequest: EMPLeadRequestArgs = {
            firstName: values.fname,
            lastName: values.lname,
            phone: values.phone,
            email: values.email,
            companyName: values.company,
            zip: values.zip,
            employees: values.employees,
            allianceId: selectedAllid,
            leadType: leadtype,
            fixedAllianceId: fixedallianceid,
            leadSource: isSmallGroup ? 'TRAD' : 'LRG',
            tcpaTemplate: isSmallGroup ? '' : 'smbLargeGroup',
        };

        try {
            const lead = await createLead(leadRequest);
            if (lead?.leadId) {
                dispatch(updateLead(String(lead.leadId), ''));
                onSuccess();
                fireTrackEvent({
                    action: 'get quote',
                    leadInfo: {
                        SMBLeadID: lead.leadId,
                        requestedDocument: 'Employer Lead Form',
                        email: values.email,
                        smbHeadcount: EMPLOYEE_OPTIONS[values.employees],
                    },
                    events: ['smblead', 'save_results'],
                });
                setWasSubmitted(true);
            } else {
                throw lead.error;
            }
        } catch (err: unknown) {
            relayApiErrorToFormik(err as apiError);
            let sentryError;
            if (typeof err === 'string') {
                sentryError = new Error(err);
                console.error('Lead error:', err);
            } else if (err instanceof Error) {
                sentryError = new Error(err.message);
                console.error('Lead error:', err.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'EmployerLeadForm',
                        event: 'createLead',
                    },
                });
            }
            onError();
        } finally {
            setIsLoading(false);
        }
    };

    const relayApiErrorToFormik = (err: apiError) => {
        if (err.type === 'PARAMETER_INVALID') {
            if (err.message?.includes('{phone=error}')) {
                formik.setErrors({ phone: 'Invalid value' });
            }
            if (err.message?.includes('{zip=error}')) {
                formik.setErrors({ zip: 'Invalid value' });
            }
            if (err.message?.includes('{employees=error}')) {
                formik.setErrors({ employees: 'Invalid value' });
            }
        }
    };

    const formik = useFormik({
        initialValues: INITIAL_VALUES.current,
        validationSchema: EmployerLeadFormSchema,
        validateOnBlur: true,
        onSubmit: handleSubmit,
    });

    const INPUTS = useMemo(() => {
        const options = Object.keys(EMPLOYEE_OPTIONS).reduce((acc: SelectOptions[], key) => {
            acc.push({
                label: EMPLOYEE_OPTIONS[key],
                value: key,
                selected: key === initialEmployees,
            });
            return acc;
        }, []);
        const inputList: InputProps[] = [
            {
                id: `fname`,
                name: 'fname',
                label: 'First name',
                disabled: disabled,
                required: true,
                maxChar: 50,
                autoComplete: 'given-name',
                type: 'text',
                qaInput: 'fname-textbox',
                ariaLabel,
                invalidMsg: 'Invalid first name',
            },
            {
                id: `lname`,
                name: 'lname',
                label: 'Last name',
                disabled: disabled,
                required: true,
                maxChar: 50,
                autoComplete: 'family-name',
                type: 'text',
                qaInput: 'lname-textbox',
                ariaLabel,
                invalidMsg: 'Invalid last name',
            },
            {
                id: `phone_${id}`,
                name: 'phone',
                label: 'Business phone',
                disabled: disabled,
                required: true,
                minChar: 10,
                maxChar: 10,
                autoComplete: 'tel',
                type: 'tel',
                qaInput: 'phone-textbox',
                ariaLabel,
                invalidMsg: 'Invalid phone number',
            },
            {
                id: `email_${id}`,
                name: 'email',
                label: 'Email',
                disabled: disabled,
                required: true,
                autoComplete: 'email',
                type: 'text',
                qaInput: 'email-textbox',
                ariaLabel,
                invalidMsg: 'Invalid email address',
            },
            {
                id: `company`,
                name: 'company',
                label: 'Company name',
                disabled: disabled,
                required: false,
                maxChar: 80,
                autoComplete: 'organization',
                type: 'text',
                qaInput: 'company-textbox',
                ariaLabel,
                invalidMsg: 'Invalid name',
            },
            {
                id: `zip_${id}`,
                name: 'zip',
                label: 'Zip code',
                disabled: disabled,
                required: true,
                minChar: 5,
                maxChar: 5,
                autoComplete: 'postal-code',
                type: 'text',
                qaInput: 'zip-textbox',
                ariaLabel,
                invalidMsg: 'Invalid zip code',
                quarterWidth: true,
            },
            {
                id: `employees_${id}`,
                name: 'employees',
                label: '# of employees',
                title: 'Number of employees',
                disabled: disabled,
                required: true,
                type: 'select',
                qaInput: 'employees-select',
                ariaLabel,
                invalidMsg: 'missing selection',
                options,
                quarterWidth: true,
            },
        ];

        return inputList;
    }, [formik.errors, formik.isValid]);

    useEffect(() => {
        if ((hasLoaded || formik.dirty) && formik.values.employees) {
            fireTrackEvent({
                page: {
                    dropDownlist: 'number of employees',
                    dropDownItem: EMPLOYEE_OPTIONS[formik.values.employees],
                },
            });
        }
    }, [formik.dirty, formik.values.employees]);

    const SubmissionMessage = memo(() => (
        <>
            <h3 className="m-0 font-bold font-poppins text-primary text-24 leading-32 md:text-32 md:leading-40 lg:text-40 lg:leading-56 text-center w-full">
                Thanks!
            </h3>
            <p className="my-3 lg:my-5 font-semibold font-poppins text-18 leading-28 md:text-20 lg:text-24 lg:leading-32 text-center w-full">
                You’ll hear from one of our licensed benefit advisors soon.
            </p>
            <p className="m-0 font-bold font-poppins text-14 leading-20 md:text-16 md:leading-24 lg:text-18 lg:leading-28 text-center w-full">
                Can’t wait? Call now
            </p>
            <SpeakWithAgent
                headingCopy=""
                fromComponentName={`Employer Lead Form: Call Agent`}
                btnStyle="secondary"
                textAlign="center"
                icon="primary-light"
                tty={false}
            />
        </>
    ));
    SubmissionMessage.displayName = 'SubmissionMessage';

    return (
        <div
            className={classNames(
                'flex flex-col items-center justify-start',
                'h-full lg:px-4 mx-auto w-full',
                wasSubmitted ? 'justify-center' : 'justify-start'
            )}
        >
            {wasSubmitted ? (
                <SubmissionMessage />
            ) : (
                <>
                    <form
                        noValidate
                        className={classNames(
                            classes,
                            'grid grid-cols-4 auto-rows-min gap-x-7 gap-y-3 w-full'
                        )}
                        onSubmit={formik.handleSubmit}
                    >
                        {INPUTS.map((item) => {
                            const isFieldValid = !(
                                formik.touched[item.name] && formik.errors[item.name]
                            );

                            if (item.type === 'select') {
                                return (
                                    <div
                                        key={item.name}
                                        className={classNames(
                                            inputClasses,
                                            'grid grid-cols-1 grid-rows-3-min-content items-start col-span-4 md:col-span-2 gap-y-1 w-full',
                                            item.fullWidth && 'lg:col-span-4',
                                            item.quarterWidth && 'lg:col-span-1'
                                        )}
                                    >
                                        <label
                                            htmlFor={item.id}
                                            className={classNames(
                                                'font-poppins text-14 leading-20',
                                                `text-${labelTextColor || '[#F0F0F0]'}`,
                                                isLabelHidden && 'sr-only',
                                                item.quarterWidth &&
                                                    ' overflow-hidden whitespace-nowrap'
                                            )}
                                            title={item.title || item.label}
                                        >
                                            {item.label}
                                        </label>
                                        <select
                                            className={classNames(
                                                'bg-white border rounded placeholder-gray text-black h-12 w-full font-poppins',
                                                'duration-500 ease-in-out transition-colors',
                                                isFieldValid
                                                    ? 'border-gray'
                                                    : 'border-red bg-red-lightest border-2 mb-1',
                                                isMobile && isMobileSideBySide
                                                    ? 'p-2 leading-5'
                                                    : 'p-3 leading-22',
                                                `mt-${formMarginTop || '0'} z-10`
                                            )}
                                            id={item.id}
                                            name={item.name}
                                            data-testid={item.name}
                                            value={formik.values[item.name]}
                                            data-qa={item.qaInput}
                                            aria-label={item.ariaLabel}
                                            disabled={item.disabled}
                                            required={item.required}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        >
                                            {item.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div
                                            className={classNames(
                                                'duration-500 ease-in-out transition-all',
                                                isFieldValid ? 'h-0 invisible' : 'h-5 visible',
                                                'flex items-center justify-start overflow-hidden w-full'
                                            )}
                                            data-qa={`${item.name}-error-message`}
                                        >
                                            <img
                                                src={errorIcon}
                                                alt=""
                                                aria-hidden
                                                width={16}
                                                className="mr-1"
                                                data-qa="error-message-icon"
                                                loading="lazy"
                                            />
                                            <span
                                                className={`text-sm font-poppins ${errorTextColor}`}
                                                data-qa="error-message-text"
                                                aria-live="polite"
                                            >
                                                {formik.errors[item.name]}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={item.id}
                                    className={classNames(
                                        inputClasses,
                                        'grid grid-cols-1 grid-rows-3-min-content items-start col-span-4 md:col-span-2 gap-y-1 w-full',
                                        item.fullWidth && 'lg:col-span-4',
                                        item.quarterWidth && 'lg:col-span-1'
                                    )}
                                >
                                    <label
                                        htmlFor={item.id}
                                        className={classNames(
                                            'font-poppins text-14 leading-20',
                                            `text-${labelTextColor || '[#F0F0F0]'}`,
                                            isLabelHidden && 'sr-only'
                                        )}
                                    >
                                        {item.label}
                                    </label>
                                    <input
                                        className={classNames(
                                            'bg-white border rounded placeholder-gray text-black h-12 w-full font-poppins',
                                            'duration-500 ease-in-out transition-colors',
                                            isFieldValid
                                                ? 'border-gray'
                                                : 'border-red bg-red-lightest border-2 mb-1',
                                            isMobile && isMobileSideBySide
                                                ? 'p-2 leading-5'
                                                : 'p-3 leading-22',
                                            `mt-${formMarginTop || '0'} z-10`
                                        )}
                                        id={item.id}
                                        name={item.name}
                                        data-testid={item.name}
                                        autoComplete={item.autoComplete}
                                        onChange={(evt) => {
                                            if (item.maxChar) {
                                                if (evt.target.value?.length <= item.maxChar) {
                                                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                    formik.setFieldValue(
                                                        item.name,
                                                        evt.target.value
                                                    );
                                                }
                                            } else {
                                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                formik.setFieldValue(item.name, evt.target.value);
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        onKeyUp={item.onKeyUp}
                                        type={item.type}
                                        value={formik.values[item.name]}
                                        data-qa={item.qaInput}
                                        aria-label={item.ariaLabel}
                                        placeholder={item.placeholder}
                                        disabled={item.disabled}
                                        required={item.required}
                                        min={item.minChar}
                                        max={item.maxChar}
                                    />
                                    <div
                                        className={classNames(
                                            'duration-500 ease-in-out transition-all',
                                            isFieldValid ? 'h-0 invisible' : 'h-5 visible',
                                            'flex items-center justify-start overflow-hidden w-full'
                                        )}
                                        data-qa={`${item.name}-error-message`}
                                    >
                                        <img
                                            src={errorIcon}
                                            alt=""
                                            aria-hidden
                                            width={16}
                                            className="mr-1"
                                            data-qa="error-message-icon"
                                            loading="lazy"
                                        />
                                        <span
                                            className={`text-sm font-poppins ${errorTextColor}`}
                                            data-qa="error-message-text"
                                            aria-live="polite"
                                        >
                                            {formik.errors[item.name]}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                        <Button
                            type="submit"
                            data-wa-link={waTracking}
                            data-qa={qaButton}
                            className={classNames(
                                buttonClasses,
                                (!isStacked || isMobileSideBySide) && 'col-span-4',
                                isMobile && isMobileSideBySide ? '!p-2 !h-auto' : '',
                                'mt-3 md:max-w-[17.625rem] mx-auto w-full'
                            )}
                            styletype={
                                buttonStyletype !== undefined
                                    ? buttonStyletype
                                    : version !== 'b'
                                        ? 'primary'
                                        : 'orange'
                            }
                            disabled={disabled}
                        >
                            <span className="font-poppins">
                                {isLoading ? (
                                    <Spinner color="white" />
                                ) : ctacopy ? (
                                    ctacopy
                                ) : buttonText ? (
                                    buttonText
                                ) : (
                                    'Get a quote'
                                )}
                            </span>
                        </Button>
                    </form>
                    <p className="mt-6 text-12 leading-4 w-full font-sans">
                        You agree eHealth may contact you regarding our insurance selection services, including by email or by calling or texting any phone number you provide eHealth, via autodialer or artificial/prerecorded voice to market insurance options. Standard message and data rates apply. This is not a condition of purchase or enrollment. Your information and use of this site is governed by our most recent{' '}
                        <a
                            className="font-sans text-primary underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.${process.env.GATSBY_SUBDOMAIN}ehealthinsurance.com/about-ehealth/terms-of-use/`}
                        >
                            Terms of Use
                        </a>
                        {' and '}
                        <a
                            className="font-sans text-primary underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.${process.env.GATSBY_SUBDOMAIN}ehealthinsurance.com/about-ehealth/privacy-policy/`}
                        >
                            Privacy Policy
                        </a>.
                    </p>
                </>
            )}
        </div>
    );
};

export default EmployerLeadForm;
